import { CloudHttpClient, type CloudHttpOptions } from "./cloud-http.ts";

export interface NoteBook {
  id: string;
  name: string | null;
  sessionId: string | null;
  isInbox: boolean;
  pinned: boolean;
  blockCount: number;
  latestBlockFirstLine: string | null;
  revision: number;
  createdAt: string;
  updatedAt: string;
}

export interface NoteBlock {
  id: string;
  noteId: string;
  content: string;
  contentRevision: number;
  enabled: boolean;
  lastSentSessionId: string | null;
  lastSentAt: string | null;
  lastSentRevision: number | null;
  mergeSourceNoteId: string | null;
  mergeSourceTitle: string | null;
  mergeSourceBlockId: string | null;
  mergedAt: string | null;
  revision: number;
  createdAt: string;
  updatedAt: string;
}

export interface NoteBookDetail {
  note: NoteBook;
  blocks: NoteBlock[];
  totalBlocks: number;
}

export interface NoteMutationResult {
  note: NoteBookDetail;
  affectedBlockIds: string[];
}

export interface NotesClientOptions extends Omit<CloudHttpOptions, "cloudUrl"> {
  baseUrl: string;
  wireFormat?: "camelCase" | "snake_case";
}

export interface CreateNoteInput {
  operationId?: string;
  name?: string;
  sessionId?: string;
  isInbox?: boolean;
  pinned?: boolean;
}

export interface UpdateNoteInput {
  operationId?: string;
  name?: string | null;
  sessionId?: string | null;
  pinned?: boolean;
  expectedRevision: number;
}

export interface CreateNoteBlockInput {
  operationId?: string;
  content: string;
  enabled?: boolean;
  expectedNoteRevision?: number;
}

export interface UpdateNoteBlockInput {
  operationId?: string;
  content?: string;
  enabled?: boolean;
  expectedRevision: number;
}

export interface NoteBlockActionInput {
  operationId?: string;
  action: "move" | "merge";
  targetNoteId: string;
  targetBlockId?: string;
  expectedRevision: number;
}

export class NotesClient {
  private readonly http: CloudHttpClient;
  private readonly root = "/v1/notes";
  private readonly wireFormat: "camelCase" | "snake_case";

  constructor(options: NotesClientOptions) {
    this.http = new CloudHttpClient({
      cloudUrl: options.baseUrl,
      accessToken: options.accessToken,
      recoverUnauthorized: options.recoverUnauthorized,
      requestTimeoutMs: options.requestTimeoutMs,
      fetcher: options.fetcher,
      client: options.client,
    });
    this.wireFormat = options.wireFormat ?? "camelCase";
  }

  async listNotes(sessionId?: string): Promise<NoteBook[]> {
    const queryKey = this.wireFormat === "camelCase" ? "sessionId" : "session_id";
    const query = new URLSearchParams();
    if (sessionId) query.set(queryKey, sessionId);
    const suffix = query.size ? `?${query.toString()}` : "";
    const value = await this.http.request(`${this.root}${suffix}`);
    if (Array.isArray(value)) return value.map(noteBook);
    const root = record(value);
    return array(field(root, "notes")).map(noteBook);
  }

  async getNote(noteId: string, offset = 0, limit = 1_000): Promise<NoteBookDetail> {
    if (!Number.isSafeInteger(offset) || offset < 0) throw new TypeError("offset must be non-negative");
    if (!Number.isSafeInteger(limit) || limit < 1 || limit > 1_000) throw new TypeError("limit must be between 1 and 1000");
    return noteDetail(await this.http.request(
      `${this.root}/${id(noteId)}?offset=${offset}&limit=${limit}`,
    ));
  }

  async createNote(input: CreateNoteInput = {}): Promise<NoteBookDetail> {
    return noteDetail(await this.http.request(this.root, {
      method: "POST",
      body: JSON.stringify(this.body({
        operationId: input.operationId,
        name: input.name,
        sessionId: input.sessionId,
        isInbox: input.isInbox ?? false,
        pinned: input.pinned ?? false,
      })),
    }));
  }

  async updateNote(noteId: string, input: UpdateNoteInput): Promise<NoteBookDetail> {
    return noteDetail(await this.http.request(`${this.root}/${id(noteId)}`, {
      method: "POST",
      body: JSON.stringify(this.body(input)),
    }));
  }

  async deleteNote(noteId: string, expectedRevision: number, operationId?: string): Promise<void> {
    await this.http.request(`${this.root}/${id(noteId)}`, {
      method: "DELETE",
      body: JSON.stringify(this.body({ operationId, expectedRevision })),
    });
  }

  async createBlock(noteId: string, input: CreateNoteBlockInput): Promise<NoteMutationResult> {
    return mutation(await this.http.request(`${this.root}/${id(noteId)}/blocks`, {
      method: "POST",
      body: JSON.stringify(this.body({ ...input, enabled: input.enabled ?? true })),
    }));
  }

  async updateBlock(blockId: string, input: UpdateNoteBlockInput): Promise<NoteMutationResult> {
    return mutation(await this.http.request(this.blockPath(blockId), {
      method: "POST",
      body: JSON.stringify(this.body(input)),
    }));
  }

  async deleteBlock(blockId: string, expectedRevision: number, operationId?: string): Promise<NoteMutationResult> {
    return mutation(await this.http.request(this.blockPath(blockId), {
      method: "DELETE",
      body: JSON.stringify(this.body({ operationId, expectedRevision })),
    }));
  }

  async reorderBlocks(noteId: string, orderedBlockIds: string[], expectedNoteRevision: number, operationId?: string): Promise<NoteMutationResult> {
    return mutation(await this.http.request(`${this.root}/${id(noteId)}/blocks/order`, {
      method: "POST",
      body: JSON.stringify(this.body({ operationId, orderedBlockIds, expectedNoteRevision })),
    }));
  }

  async markSent(
    noteId: string,
    sessionId: string,
    blockIds: string[],
    expectedRevision: number,
    operationId?: string,
  ): Promise<NoteMutationResult> {
    return mutation(await this.http.request(`${this.root}/${id(noteId)}/mark-sent`, {
      method: "POST",
      body: JSON.stringify(this.body({ operationId, sessionId, blockIds, expectedRevision })),
    }));
  }

  async actOnBlock(blockId: string, input: NoteBlockActionInput): Promise<NoteMutationResult> {
    return mutation(await this.http.request(`${this.blockPath(blockId)}/action`, {
      method: "POST",
      body: JSON.stringify(this.body(input)),
    }));
  }

  async mergeNote(noteId: string, targetNoteId: string, expectedRevision: number, operationId?: string): Promise<NoteMutationResult> {
    return mutation(await this.http.request(`${this.root}/${id(noteId)}/merge`, {
      method: "POST",
      body: JSON.stringify(this.body({ operationId, targetNoteId, expectedRevision })),
    }));
  }

  private blockPath(blockId: string): string {
    return `/v1/note-blocks/${id(blockId)}`;
  }

  private body(input: object): Record<string, unknown> {
    const entries = Object.entries(input as Record<string, unknown>).filter(([, value]) => value !== undefined);
    if (this.wireFormat === "camelCase") return Object.fromEntries(entries);
    return Object.fromEntries(entries.map(([key, value]) => [snakeCase(key), value]));
  }
}

function noteBook(value: unknown): NoteBook {
  const row = record(value);
  return {
    id: text(field(row, "noteId", "note_id", "id")),
    name: nullableText(field(row, "name")),
    sessionId: nullableText(field(row, "sessionId", "session_id")),
    isInbox: boolean(field(row, "isInbox", "is_inbox")),
    pinned: booleanOrDefault(field(row, "pinned"), false),
    blockCount: integer(field(row, "blockCount", "block_count")),
    latestBlockFirstLine: nullableText(field(row, "latestBlockFirstLine", "latest_block_first_line")),
    revision: integer(field(row, "revision")),
    createdAt: text(field(row, "createdAt", "created_at")),
    updatedAt: text(field(row, "updatedAt", "updated_at")),
  };
}

function noteBlock(value: unknown): NoteBlock {
  const row = record(value);
  const revision = integer(field(row, "revision"));
  return {
    id: text(field(row, "blockId", "block_id", "id")),
    noteId: text(field(row, "noteId", "note_id")),
    content: text(field(row, "content")),
    contentRevision: integerOrDefault(
      field(row, "contentRevision", "content_revision"),
      revision,
    ),
    enabled: booleanOrDefault(field(row, "enabled"), true),
    lastSentSessionId: nullableText(field(row, "lastSentSessionId", "last_sent_session_id")),
    lastSentAt: nullableText(field(row, "lastSentAt", "last_sent_at")),
    lastSentRevision: nullableInteger(field(row, "lastSentRevision", "last_sent_revision")),
    mergeSourceNoteId: nullableText(field(row, "mergeSourceNoteId", "merge_source_note_id")),
    mergeSourceTitle: nullableText(field(row, "mergeSourceTitle", "merge_source_title")),
    mergeSourceBlockId: nullableText(field(row, "mergeSourceBlockId", "merge_source_block_id")),
    mergedAt: nullableText(field(row, "mergedAt", "merged_at")),
    revision,
    createdAt: text(field(row, "createdAt", "created_at")),
    updatedAt: text(field(row, "updatedAt", "updated_at")),
  };
}

function noteDetail(value: unknown): NoteBookDetail {
  const root = record(value);
  return {
    note: noteBook(field(root, "note")),
    blocks: array(field(root, "blocks")).map(noteBlock),
    totalBlocks: integer(field(root, "totalBlocks", "total_blocks")),
  };
}

function mutation(value: unknown): NoteMutationResult {
  const root = record(value);
  return {
    note: noteDetail(field(root, "note")),
    affectedBlockIds: array(field(root, "affectedBlockIds", "affected_block_ids")).map(text),
  };
}

function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new TypeError("Expected an object");
  return value as Record<string, unknown>;
}
function array(value: unknown): unknown[] { if (!Array.isArray(value)) throw new TypeError("Expected an array"); return value; }
function text(value: unknown): string { if (typeof value !== "string") throw new TypeError("Expected text"); return value; }
function nullableText(value: unknown): string | null { return value === undefined || value === null ? null : text(value); }
function boolean(value: unknown): boolean { if (typeof value !== "boolean") throw new TypeError("Expected boolean"); return value; }
function booleanOrDefault(value: unknown, fallback: boolean): boolean {
  return value === undefined ? fallback : boolean(value);
}
function integer(value: unknown): number { if (!Number.isSafeInteger(value) || Number(value) < 0) throw new TypeError("Expected a non-negative integer"); return Number(value); }
function integerOrDefault(value: unknown, fallback: number): number {
  return value === undefined ? fallback : integer(value);
}
function nullableInteger(value: unknown): number | null {
  return value === undefined || value === null ? null : integer(value);
}
function field(row: Record<string, unknown>, ...keys: string[]): unknown {
  for (const key of keys) if (Object.hasOwn(row, key)) return row[key];
  return undefined;
}
function id(value: string): string { const trimmed = value.trim(); if (!trimmed) throw new TypeError("ID is required"); return encodeURIComponent(trimmed); }
function snakeCase(value: string): string { return value.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`); }
